import { useMemo, useState } from "react";
import { useAsync } from "../hooks/useAsync";
import { listExercises } from "../api/endpoints";
import { Card, EmptyState, ErrorMessage, PageHeading, Spinner } from "../components/ui";

export default function ExercisesPage() {
  const { data, error, loading, reload } = useAsync(listExercises, []);
  const [query, setQuery] = useState("");

  const items = data?.items ?? [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((e) => e.name.toLowerCase().includes(q));
  }, [items, query]);

  return (
    <div data-testid="exercises-page">
      <PageHeading title="Exercises" />

      {loading && <Spinner />}
      {error && <ErrorMessage error={error} onRetry={reload} />}

      {data && items.length === 0 && (
        <EmptyState
          testId="exercises-empty"
          title="No exercises available"
          description="The exercise catalogue is empty. Once it is populated, exercises will appear here."
        />
      )}

      {items.length > 0 && (
        <>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search exercises"
            aria-label="Search exercises"
            className="mb-4 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-emerald-600 focus:outline-none dark:border-[#2e2e2e] dark:bg-[#111111] dark:text-gray-100"
            data-testid="exercises-input-search"
          />

          {filtered.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-400" data-testid="exercises-no-match">
              No exercises match “{query}”.
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {filtered.map((ex) => (
                <Card key={ex.id} className="p-4" data-testid={`exercises-item-${ex.id}`}>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{ex.name}</p>
                  {ex.description && (
                    <p className="mt-1 line-clamp-2 text-xs text-gray-600 dark:text-gray-400">
                      {ex.description}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {[ex.complexity, ex.movementType, ex.equipmentType]
                      .filter(Boolean)
                      .map((meta) => (
                        <span
                          key={meta}
                          className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] capitalize text-gray-600 dark:bg-[#2e2e2e] dark:text-gray-400"
                        >
                          {String(meta).replace(/_/g, " ")}
                        </span>
                      ))}
                  </div>
                  {ex.videoUrl && (
                    <a
                      href={ex.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-block text-xs font-medium text-emerald-700 hover:underline dark:text-emerald-400"
                      data-testid={`exercises-video-${ex.id}`}
                    >
                      Watch demo
                    </a>
                  )}
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
